import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BoxInfo, BoxGiftInfo, CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import { santaClient } from '@src/state/client';
import { loadSession } from '@src/state/sessionSlice';

export type BoxStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface BoxState {
  key: string | null;
  /** Invite key from `?join=`. Every box request needs it for a visitor. */
  join: string;
  status: BoxStatus;
  /** API error code — UNAUTHORIZED_FOR_BOX and BOX_NOT_FOUND are screens. */
  error: string | null;
  info: BoxInfo | null;
  cards: CardInfo[];
  /** The viewer's own card, when they are a participant. */
  card: FullCardInfo | null;
  /** Who the viewer gives a gift to. Only readable once they have revealed it. */
  ward: FullCardInfo | null;
}

const initialState: BoxState = {
  key: null,
  join: '',
  status: 'idle',
  error: null,
  info: null,
  cards: [],
  card: null,
  ward: null,
};

interface LoadBoxArgs {
  key: string;
  join?: string;
}

interface LoadedBox {
  key: string;
  join: string;
  info: BoxInfo;
  cards: CardInfo[];
  card: FullCardInfo | null;
  ward: FullCardInfo | null;
}

/**
 * Everything a box screen needs, in one dispatch. Box info decides who the
 * viewer is, so the calls that depend on being a participant are made only
 * after it comes back — an organizer has no card of their own to fetch.
 */
export const loadBox = createAsyncThunk<LoadedBox, LoadBoxArgs>(
  'box/load',
  async ({ key, join = '' }, { rejectWithValue, dispatch }) => {
    let info = await santaClient.getBoxInfo({ key, join });
    if (!info.ok && info.error?.message === 'UNAUTHORIZED_FOR_BOX') {
      // A different guest may be active after creating or joining another game.
      // Only restore an unambiguous profile already authenticated in this browser.
      const session = await santaClient.session();
      const matches =
        session.data?.profiles.filter(
          (profile) =>
            profile.id !== session.data?.id && profile.boxes.some((box) => box.key === key),
        ) ?? [];
      if (session.ok && matches.length === 1) {
        const activated = await santaClient.activateProfile({ userId: matches[0].id });
        if (activated.ok) {
          await dispatch(loadSession());
          info = await santaClient.getBoxInfo({ key, join });
        }
      }
    }
    if (!info.ok || !info.data) {
      return rejectWithValue(info.error?.message ?? 'errors.UNKNOWN');
    }

    // One shared snapshot serves management tabs and actions until the box refreshes.
    const cards = await santaClient.getCards({ key, join });
    const isParticipant = Boolean(info.data.isParticipant);
    const [card, ward] = await Promise.all([
      isParticipant ? santaClient.getCard({ key, join }) : null,
      // The giftee is only worth asking for once this participant has looked:
      // before that the endpoint is a 403 and the card shows a question mark.
      isParticipant && info.data.isActiveDraw ? santaClient.getWard({ key }) : null,
    ]);

    return {
      key,
      join,
      info: info.data,
      cards: cards?.ok && cards.data ? cards.data : [],
      card: card?.ok ? (card.data ?? null) : null,
      ward: ward?.ok ? (ward.data ?? null) : null,
    };
  },
);

const boxSlice = createSlice({
  name: 'box',
  initialState,
  reducers: {
    giftOpened(state, action: PayloadAction<{ key: string; cardId: number; gift: BoxGiftInfo }>) {
      if (state.info?.box.key === action.payload.key && state.info.cardId === action.payload.cardId && state.info.gift?.id === action.payload.gift.id) {
        state.info.gift = action.payload.gift;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadBox.pending, (state, action) => {
        // A reload after an action (renaming, deleting) must not blank the
        // screen, so what is on display survives until the new data lands.
        state.status = 'loading';
        state.error = null;
        state.key = action.meta.arg.key;
        state.join = action.meta.arg.join ?? '';
      })
      .addCase(loadBox.fulfilled, (state, action) => {
        state.status = 'ready';
        state.key = action.payload.key;
        state.join = action.payload.join;
        state.info = action.payload.info;
        state.cards = action.payload.cards;
        state.card = action.payload.card;
        state.ward = action.payload.ward;
      })
      .addCase(loadBox.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          (action.payload as string | undefined) ?? action.error.message ?? 'errors.UNKNOWN';
        state.info = null;
        state.cards = [];
        state.card = null;
        state.ward = null;
      });
  },
});

export const { giftOpened } = boxSlice.actions;
export default boxSlice.reducer;
